import React, { useState } from 'react';
import {
  Box, Chip, TextField, InputAdornment, Button, Collapse, MenuItem, Autocomplete, Stack, Badge,
} from '@mui/material';
import { Search as SearchIcon, Tune as TuneIcon } from '@mui/icons-material';

export type Periodo = 'todos' | 'ate_hoje' | 'vencidos' | 'hoje' | '7dias' | 'intervalo';
export type Ordem = 'venc_asc' | 'venc_desc' | 'valor_desc' | 'valor_asc' | 'fav';

export interface FiltrosState {
  periodo: Periodo;
  de: string;
  ate: string;
  status: 'todos' | 'aguardando' | 'conferido' | 'processando' | 'repetidos';
  forma: 'todas' | 'pix_chave' | 'pix_qr' | 'boleto' | 'arrecadacao';
  via: 'todas' | 'api' | 'app';
  favorecido: string | null;
  busca: string;
  valorMin: string;
  valorMax: string;
  ordem: Ordem;
}

export const FILTROS_INICIAIS: FiltrosState = {
  periodo: 'ate_hoje', de: '', ate: '', status: 'todos', forma: 'todas', via: 'todas',
  favorecido: null, busca: '', valorMin: '', valorMax: '', ordem: 'venc_asc',
};

const PERIODOS: { v: Periodo; l: string }[] = [
  { v: 'todos', l: 'Todos' },
  { v: 'ate_hoje', l: 'Vencidos + hoje' },
  { v: 'vencidos', l: 'Vencidos' },
  { v: 'hoje', l: 'Hoje' },
  { v: '7dias', l: 'Próx. 7 dias' },
  { v: 'intervalo', l: 'Intervalo' },
];

interface Props {
  value: FiltrosState;
  onChange: (f: FiltrosState) => void;
  favorecidos: string[];
  contagem: Partial<Record<Periodo, number>>;
}

const Filtros: React.FC<Props> = ({ value, onChange, favorecidos, contagem }) => {
  const [mais, setMais] = useState(false);
  const set = <K extends keyof FiltrosState>(k: K, v: FiltrosState[K]) => onChange({ ...value, [k]: v });
  const ativos = [
    value.status !== 'todos', value.forma !== 'todas', value.via !== 'todas', !!value.favorecido, !!value.valorMin, !!value.valorMax,
  ].filter(Boolean).length;

  return (
    <Box>
      <Box sx={{ display: 'flex', gap: 0.75, overflowX: 'auto', pb: 1, mx: -0.5, px: 0.5, '&::-webkit-scrollbar': { display: 'none' } }}>
        {PERIODOS.map((p) => (
          <Chip key={p.v} clickable color={value.periodo === p.v ? 'primary' : 'default'} variant={value.periodo === p.v ? 'filled' : 'outlined'}
            label={contagem[p.v] != null ? `${p.l} · ${contagem[p.v]}` : p.l} onClick={() => set('periodo', p.v)} sx={{ flexShrink: 0 }} />
        ))}
      </Box>
      {value.periodo === 'intervalo' && (
        <Stack direction="row" spacing={1} sx={{ mb: 1 }}>
          <TextField size="small" type="date" label="De" value={value.de} onChange={(e) => set('de', e.target.value)} InputLabelProps={{ shrink: true }} fullWidth />
          <TextField size="small" type="date" label="Até" value={value.ate} onChange={(e) => set('ate', e.target.value)} InputLabelProps={{ shrink: true }} fullWidth />
        </Stack>
      )}
      <Stack direction="row" spacing={1} alignItems="center">
        <TextField size="small" fullWidth placeholder="Buscar favorecido, descrição, código…" value={value.busca}
          onChange={(e) => set('busca', e.target.value)}
          InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment> }} sx={{ bgcolor: '#fff' }} />
        <Badge badgeContent={ativos} color="primary">
          <Button variant={mais ? 'contained' : 'outlined'} onClick={() => setMais((v) => !v)} sx={{ minWidth: 0, px: 1.5, height: 40 }}>
            <TuneIcon fontSize="small" />
          </Button>
        </Badge>
      </Stack>
      <Collapse in={mais}>
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr 1fr', md: 'repeat(4, 1fr)' }, gap: 1, mt: 1.5 }}>
          <TextField select size="small" label="Situação" value={value.status} onChange={(e) => set('status', e.target.value as FiltrosState['status'])} sx={{ bgcolor: '#fff' }}>
            <MenuItem value="todos">Todas</MenuItem>
            <MenuItem value="aguardando">Aguardando aprovação</MenuItem>
            <MenuItem value="conferido">Já conferidos</MenuItem>
            <MenuItem value="processando">Processando</MenuItem>
            <MenuItem value="repetidos">Repetidos</MenuItem>
          </TextField>
          <TextField select size="small" label="Como se paga" value={value.via} onChange={(e) => set('via', e.target.value as FiltrosState['via'])} sx={{ bgcolor: '#fff' }}>
            <MenuItem value="todas">Todos</MenuItem>
            <MenuItem value="api">Paga pela API</MenuItem>
            <MenuItem value="app">Pagar no app</MenuItem>
          </TextField>
          <TextField select size="small" label="Forma" value={value.forma} onChange={(e) => set('forma', e.target.value as FiltrosState['forma'])} sx={{ bgcolor: '#fff' }}>
            <MenuItem value="todas">Todas</MenuItem>
            <MenuItem value="pix_chave">PIX por chave</MenuItem>
            <MenuItem value="pix_qr">PIX copia e cola / QR</MenuItem>
            <MenuItem value="boleto">Boleto</MenuItem>
            <MenuItem value="arrecadacao">Guia / conta de consumo</MenuItem>
          </TextField>
          <TextField select size="small" label="Ordenar" value={value.ordem} onChange={(e) => set('ordem', e.target.value as Ordem)} sx={{ bgcolor: '#fff' }}>
            <MenuItem value="venc_asc">Vencimento (mais antigo)</MenuItem>
            <MenuItem value="venc_desc">Vencimento (mais novo)</MenuItem>
            <MenuItem value="valor_desc">Maior valor</MenuItem>
            <MenuItem value="valor_asc">Menor valor</MenuItem>
            <MenuItem value="fav">Favorecido (A–Z)</MenuItem>
          </TextField>
          <Autocomplete size="small" options={favorecidos} value={value.favorecido} onChange={(_, v) => set('favorecido', v)}
            renderInput={(p) => <TextField {...p} label="Favorecido" sx={{ bgcolor: '#fff' }} />} sx={{ gridColumn: { xs: '1 / -1', md: 'span 2' } }} />
          <TextField size="small" label="Valor mín." value={value.valorMin} onChange={(e) => set('valorMin', e.target.value.replace(/[^\d,.]/g, ''))}
            inputProps={{ inputMode: 'decimal' }} sx={{ bgcolor: '#fff' }} />
          <TextField size="small" label="Valor máx." value={value.valorMax} onChange={(e) => set('valorMax', e.target.value.replace(/[^\d,.]/g, ''))}
            inputProps={{ inputMode: 'decimal' }} sx={{ bgcolor: '#fff' }} />
        </Box>
        <Box sx={{ textAlign: 'right', mt: 1 }}>
          <Button size="small" onClick={() => onChange({ ...FILTROS_INICIAIS })}>Limpar filtros</Button>
        </Box>
      </Collapse>
    </Box>
  );
};

export default Filtros;
